import { UMButton, UMIcon, UMR, UMToolbarIconButton, UMToolbarInput } from '../_components';
import type { SelectionTool } from './types';

const BUILDING_TOOLS: { id: SelectionTool; icon: string; label: string }[] = [
  { id: 'add-one', icon: 'domain_add', label: 'Ajouter un bâtiment' },
  { id: 'add-zone', icon: 'highlight_alt', label: 'Ajouter des bâtiments par zone' },
  { id: 'remove-one', icon: 'domain_disabled', label: 'Retirer un bâtiment' },
  { id: 'remove-zone', icon: 'remove_selection', label: 'Retirer des bâtiments par zone' },
];

export function SelectionToolbar({ pendingCount, onValidate, onCancel, blocked = false, search, onSearchChange, onCenter, onMessage, activeTool, onToolChange, selectionLabel, onQuit }: {
  pendingCount: number;
  onValidate: () => void;
  onCancel: () => void;
  blocked?: boolean;
  selectionLabel: string;
  search: string;
  onSearchChange: (value: string) => void;
  onCenter: () => void;
  onMessage: (message: string) => void;
  activeTool: SelectionTool | null;
  onToolChange: (id: SelectionTool) => void;
  onQuit?: () => void;
}) {
  return (
    <div data-selection-toolbar="" style={{
      pointerEvents: 'auto', display: 'flex', alignItems: 'center',
      maxWidth: 'calc(100vw - 80px)', boxSizing: 'border-box',
      gap: UMR.size.toolbarPadding, padding: UMR.size.toolbarPadding,
      background: UMR.white, borderBottom: `1px solid ${UMR.border}`,
      borderRight: `1px solid ${UMR.border}`, fontFamily: UMR.font, position: 'relative',
    }}>
      <style>{`
        .selection-toolbar-search > div { width: 100% !important; min-width: 0; }
        [data-um-shell] nav[aria-label="Navigation UrbanMap"] button[aria-pressed="true"] {
          background: ${UMR.railHover} !important;
          box-shadow: inset -4px 0 ${UMR.blue};
          color: ${UMR.blue} !important;
        }
        [data-um-shell] nav[aria-label="Navigation UrbanMap"] button[aria-pressed="true"] span {
          color: ${UMR.blue} !important;
        }
        [data-um-shell] nav[aria-label="Navigation UrbanMap"] button[data-selection-active] { position: relative; }
        [data-um-shell] nav[aria-label="Navigation UrbanMap"] button[data-selection-active]::after {
          content: ''; position: absolute; top: 8px; left: calc(50% + 8px);
          width: 10px; height: 10px; border-radius: 50%; background: ${UMR.blue}; border: 2px solid ${UMR.white};
        }
        .selection-building-tool { background: transparent; }
        .selection-building-tool:hover:not(:disabled),
        .selection-building-tool[aria-pressed="true"] {
          background: ${UMR.hover};
        }
        .selection-building-tool:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      `}</style>
      {/* Nom de la sélection + recentrage calés sur la largeur du panneau ouvert (300px). */}
      {selectionLabel && <div style={{
        display: 'flex', alignItems: 'center', gap: UMR.size.toolbarPadding, flexShrink: 0,
        width: UMR.size.panelWidth - UMR.size.toolbarPadding, boxSizing: 'border-box',
      }}>
        <div aria-label="Sélection active" style={{
          display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1,
          height: 32, boxSizing: 'border-box', padding: '0 8px 0 12px',
          background: UMR.bluePressed, border: `1px solid ${UMR.blue}`, color: UMR.blueLink, fontSize: 14,
        }}>
          <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{selectionLabel}</span>
          <button type="button" aria-label="Quitter la sélection" title="Quitter la sélection" onClick={onQuit}
            style={{ display: 'flex', border: 'none', padding: 0, background: 'transparent', color: UMR.blueLink, cursor: 'pointer' }}>
            <UMIcon name="close" size={16} />
          </button>
        </div>
        <UMToolbarIconButton icon="center_focus_strong" label="Centrer sur ma sélection" onClick={onCenter} />
      </div>}
      <div className="selection-toolbar-search" style={{
        width: selectionLabel ? 'clamp(80px, 21vw, 300px)' : UMR.size.panelWidth - 2 * UMR.size.toolbarPadding - UMR.size.iconButton - UMR.size.toolbarPadding,
        minWidth: 80, flexShrink: 1,
      }}>
      <UMToolbarInput ariaLabel="Rechercher" placeholder="Rechercher" value={search}
        trailingIcon="search" onChange={onSearchChange}
      />
      </div>
      <UMToolbarIconButton icon="my_location" label="Me localiser"
        onClick={() => onMessage('Prototype local — la géolocalisation n’est pas sollicitée. Utilisez « Centrer sur ma sélection ».')}
      />
      {selectionLabel && <div role="group" aria-label="Actions sur les bâtiments" style={{ display: 'flex', flexShrink: 0 }}>
        {BUILDING_TOOLS.map(({ id, icon, label }) => (
          <button key={id} type="button" className="selection-building-tool"
            aria-label={label} title={label} aria-pressed={activeTool === id}
            disabled={blocked || (!!activeTool && activeTool !== id)}
            onClick={() => onToolChange(id)}
            style={{
              width: UMR.size.iconButton, height: UMR.size.iconButton,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: 'none', padding: 0, background: activeTool === id ? UMR.active : undefined,
              color: UMR.text, cursor: 'pointer', position: 'relative',
            }}
          >
            <UMIcon name={icon} size={20} />
            {activeTool === id && pendingCount > 0 && (
              <span aria-label={`${pendingCount} bâtiment(s) en attente`} style={{
                position: 'absolute', top: 2, right: 2, minWidth: 16, height: 16, padding: '0 4px', boxSizing: 'border-box',
                borderRadius: 8, background: id.startsWith('add') ? UMR.blue : 'var(--nj-core-color-red-600, #d9261c)', color: UMR.white, fontSize: 11, fontWeight: 700, lineHeight: '16px', textAlign: 'center',
              }}>{pendingCount}</span>
            )}
          </button>
        ))}
      </div>}
      {selectionLabel && activeTool && <div role="group" aria-label="Validation des bâtiments" style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
        <UMButton disabled={pendingCount === 0} onClick={onValidate}>Valider</UMButton>
        <UMButton variant="secondary" onClick={onCancel}>Annuler</UMButton>
      </div>}
    </div>
  );
}
